/**
 * capabilityGrounding — keep suggestion chips inside what Prest0n can DO.
 *
 * Owner directive 2026-08-11 (trial screenshot): a brand-new account was shown
 * "Read the newest documents in my Drive and summarize what changed" as a
 * recommended message; picking it produced "I don't have a connection to your
 * Drive from here". A recommendation Prest0n cannot fulfil is worse than no
 * recommendation — the user did exactly what the product suggested and hit a
 * wall.
 *
 * Server-side generation is now connection-aware (greetingSuggestionsCore /
 * threadFollowupCore), but chips also come from PERSISTED and CONFIGURED
 * sources that may predate a disconnect, and connection state changes live in
 * the session. This module is the render-time guarantee: given the account's
 * live connected slugs, it drops or rewrites any chip that needs an app the
 * account does not have — rewriting into the "Connect my <App> — …" form that
 * fires the in-chat one-click connect card, so the wall becomes a door.
 *
 * Pure and dependency-free; connection state is an input. `null` connected
 * state (unknown / fetch failed) grounds NOTHING — fail open, exactly like the
 * summon gate: a wrongly-hidden good suggestion is worse than a rare stale one.
 */

/** Slug → friendly name (client parity with connectApp's FRIENDLY_NAME_MAP). */
export const APP_FRIENDLY_NAMES: Record<string, string> = {
  gmail: 'Gmail', googlecalendar: 'Google Calendar', googledrive: 'Google Drive',
  googlesheets: 'Google Sheets', googledocs: 'Google Docs', googlecontacts: 'Google Contacts',
  slack: 'Slack', github: 'GitHub', notion: 'Notion', linear: 'Linear', outlook: 'Outlook',
  hubspot: 'HubSpot', salesforce: 'Salesforce', jira: 'Jira', asana: 'Asana',
  dropbox: 'Dropbox', one_drive: 'OneDrive', calendly: 'Calendly', zoom: 'Zoom',
  stripe: 'Stripe', quickbooks: 'QuickBooks', trello: 'Trello', mailchimp: 'Mailchimp',
  typeform: 'Typeform', linkedin: 'LinkedIn', youtube: 'YouTube',
};

/**
 * App-DEPENDENCY detectors. A pattern here must mean "this message needs the
 * live connection", not merely "the word appears": "Draft a follow-up email"
 * needs no app (Prest0n drafts text), while "Search my inbox" needs Gmail.
 * Conservative on purpose — a missed detection falls through to the
 * assistant's own connection-aware reply, which now offers the connect card.
 */
const APP_DEPENDENCY_PATTERNS: ReadonlyArray<{ slug: string; pattern: RegExp }> = [
  { slug: 'googledrive', pattern: /\bgoogle\s*drive\b|\b(?:my|your|the|our)\s+drive\b|\bdrive\s+(?:files?|folders?|documents?)\b/i },
  { slug: 'gmail', pattern: /\bgmail\b|\b(?:my|your|the|our)\s+inbox\b|\b(?:send|check|search|read|triage|scan|monitor|watch)\b[^.;]{0,40}\b(?:emails?|inbox|correos?)\b/i },
  { slug: 'googlecalendar', pattern: /\bgoogle\s*calendar\b|\b(?:my|your|the|our)\s+calendar\b|\b(?:schedule|book|reschedule)\b[^.;]{0,40}\b(?:meeting|call|appointment|event|cita|reuni[oó]n)\b|\bavailability\b/i },
  { slug: 'googlesheets', pattern: /\bgoogle\s*sheets?\b|\b(?:my|your|the|our)\s+spreadsheets?\b/i },
  { slug: 'googledocs', pattern: /\bgoogle\s*docs?\b/i },
  { slug: 'googlecontacts', pattern: /\bgoogle\s*contacts\b|\b(?:my|your|the|our)\s+contacts\b/i },
  { slug: 'slack', pattern: /\bslack\b/i },
  { slug: 'microsoft_teams', pattern: /\bmicrosoft\s+teams\b|\bms\s+teams\b/i },
  { slug: 'notion', pattern: /\bnotion\b/i },
  { slug: 'hubspot', pattern: /\bhubspot\b/i },
  { slug: 'salesforce', pattern: /\bsalesforce\b/i },
  { slug: 'outlook', pattern: /\boutlook\b/i },
  { slug: 'dropbox', pattern: /\bdropbox\b/i },
  { slug: 'one_drive', pattern: /\bonedrive\b|\bone\s+drive\b/i },
  { slug: 'github', pattern: /\bgithub\b/i },
  { slug: 'linear', pattern: /\blinear\s+(?:issues?|tickets?|projects?)\b/i },
  { slug: 'jira', pattern: /\bjira\b/i },
  { slug: 'asana', pattern: /\basana\b/i },
  { slug: 'trello', pattern: /\btrello\b/i },
  { slug: 'quickbooks', pattern: /\bquickbooks\b/i },
  { slug: 'stripe', pattern: /\bstripe\b/i },
  { slug: 'calendly', pattern: /\bcalendly\b/i },
  { slug: 'zoom', pattern: /\bzoom\s+(?:meeting|call|link)\b/i },
  { slug: 'mailchimp', pattern: /\bmailchimp\b/i },
  { slug: 'typeform', pattern: /\btypeform\b/i },
  { slug: 'linkedin', pattern: /\blinkedin\b/i },
  { slug: 'youtube', pattern: /\byou\s?tube\b|\b(?:my|your|the|our)\s+channel\b/i },
];

/**
 * Apps whose connection we NEVER recommend (owner directive 2026-08-12):
 * external team-chat tools compete with the platform's own group threads —
 * "we want the organization to utilize the platform and group threads
 * features as their communication platform." A chip pushing Slack/Teams is
 * dropped even if a server-side source produced one; explicit user requests
 * to connect them still work (the connect-app tool is untouched).
 */
export const NEVER_RECOMMEND_CONNECT_SLUGS: ReadonlySet<string> = new Set([
  'slack',
  'microsoft_teams',
]);

/** A chip that is already a connect request ("Connect my Gmail — …"). */
const CONNECT_CHIP = /^\s*(?:@[\w.-]+\s+)?connect(?:\s+(?:to|with))?\s+(?:my|the)?\s*/i;

/** Per-chip length ceiling after a rewrite (mirrors the server's hard cap). */
const MAX_CHIP_CHARS = 120;

/** Which integrations (slugs) this suggestion text depends on. Pure. */
export function detectRequiredIntegrations(text: string): string[] {
  const value = typeof text === 'string' ? text : '';
  if (!value.trim()) return [];
  const out: string[] = [];
  for (const { slug, pattern } of APP_DEPENDENCY_PATTERNS) {
    if (pattern.test(value) && !out.includes(slug)) out.push(slug);
  }
  return out;
}

export interface GroundSuggestionChipsOptions {
  /**
   * Whether the VIEWER may connect apps (RBAC `integrations.manage`,
   * owner-inclusive). Owner directive 2026-08-12: recommendations must fit
   * the user's permissions — false drops every connect chip AND every chip
   * blocked on an unconnected app (a rewrite the clicker cannot complete is
   * still a dead end). Default true: fail open while RBAC is loading/errored,
   * same bias as the rest of this module.
   */
  allowConnectChips?: boolean;
}

/**
 * Ground a chip list against the account's live connected slugs.
 *
 *  • `connectedSlugs === null` (unknown state) → connection grounding is
 *    skipped (fail open); the never-recommend and permission rules below
 *    still apply — they do not depend on connection state.
 *  • A chip needing only connected apps → untouched.
 *  • A connect chip for an app that is ALREADY connected → dropped (stale).
 *  • A connect chip for a never-recommend app (Slack-class team chat) →
 *    dropped, whatever produced it.
 *  • A chip needing an unconnected app → rewritten to
 *    "Connect my <App> — needed for: <original>", which fires the in-chat
 *    one-click connect card when sent. One connect chip per app; further
 *    chips blocked on the same app are dropped rather than repeated. Never
 *    rewritten toward a never-recommend app, and only when the viewer may
 *    connect (see options.allowConnectChips).
 */
export function groundSuggestionChips(
  texts: ReadonlyArray<string> | undefined,
  connectedSlugs: ReadonlyArray<string> | null | undefined,
  options?: GroundSuggestionChipsOptions,
): string[] {
  const list = Array.isArray(texts) ? texts.filter((t) => typeof t === 'string' && t.trim()) : [];
  const allowConnect = options?.allowConnectChips !== false;
  const connected = connectedSlugs == null
    ? null
    : new Set(connectedSlugs.map((s) => String(s || '').toLowerCase()));

  const out: string[] = [];
  const offeredConnects = new Set<string>();
  for (const text of list) {
    const required = detectRequiredIntegrations(text);
    const isConnectChip = CONNECT_CHIP.test(text);

    if (isConnectChip) {
      const target = required[0];
      // Never-recommend apps are dropped no matter what produced the chip.
      if (target && NEVER_RECOMMEND_CONNECT_SLUGS.has(target)) continue;
      // A viewer without the integrations permission gets no connect chips.
      if (!allowConnect) continue;
      if (connected == null) { out.push(text); continue; }
      // A connect chip for an already-connected app is stale noise; for a
      // still-unconnected app it is exactly right — keep it (once per app).
      if (target && connected.has(target)) continue;
      if (target && offeredConnects.has(target)) continue;
      if (target) offeredConnects.add(target);
      out.push(text);
      continue;
    }

    if (connected == null) { out.push(text); continue; }

    const missing = required.filter((slug) => !connected.has(slug));
    if (missing.length === 0) {
      out.push(text);
      continue;
    }

    // Blocked on an unconnected app. A rewrite only helps someone who can
    // complete the connect; never toward a team-chat app we don't recommend.
    const slug = missing[0];
    if (!allowConnect) continue;
    if (NEVER_RECOMMEND_CONNECT_SLUGS.has(slug)) continue;
    if (offeredConnects.has(slug)) continue; // one door per app, not three
    const name = APP_FRIENDLY_NAMES[slug];
    if (!name) continue; // unmappable dependency — drop rather than mislead
    offeredConnects.add(slug);
    const prefix = `Connect my ${name} — needed for: `;
    const room = Math.max(0, MAX_CHIP_CHARS - prefix.length - 1);
    const original = text.trim();
    const clipped = original.length > room
      ? `${original.slice(0, Math.max(0, room - 1)).replace(/[\s,;:.]+$/, '')}…`
      : original;
    out.push(`${prefix}${clipped}`);
  }
  return out;
}
